/**
 * Condition Auto-Clear Types
 *
 * Types for the condition auto-clearing system that matches eFolder documents
 * to open conditions and clears them via the Encompass API.
 */

/**
 * Match strategy for pattern matching
 */
export type MatchStrategy = 'contains' | 'exact' | 'starts_with' | 'regex';

/**
 * Field on the condition to match against
 */
export type ConditionMatchField = 'title' | 'externalId' | 'externalDescription';

/**
 * Action to take when a match is found
 */
export type ClearAction = 'clear' | 'received' | 'fulfilled';

/**
 * An auto-clear rule that maps a condition pattern to a document pattern
 */
export interface ConditionAutoClearRule {
  id: string;
  name: string;
  description: string | null;
  conditionMatchField: ConditionMatchField;
  conditionMatchStrategy: MatchStrategy;
  conditionMatchPattern: string;
  documentMatchStrategy: MatchStrategy;
  documentMatchPattern: string;
  documentRequiredStatus: string;
  action: ClearAction;
  requireConfirmation: boolean;
  priority: number;
  isActive: boolean;
  disclosureCheckEnabled: boolean;
  disclosureTypeRequired: string | null;
  disclosureRecipientStatus: string | null;
  disclosureEventFieldName: string | null;
  disclosureEventFieldOperator: string | null;
  disclosureEventFieldValue: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Database row format (snake_case)
 */
export interface ConditionAutoClearRuleRow {
  id: string;
  name: string;
  description: string | null;
  condition_match_field: string;
  condition_match_strategy: string;
  condition_match_pattern: string;
  document_match_strategy: string;
  document_match_pattern: string;
  document_required_status: string;
  action: string;
  require_confirmation: boolean;
  priority: number;
  is_active: boolean;
  disclosure_check_enabled: boolean;
  disclosure_type_required: string | null;
  disclosure_recipient_status: string | null;
  disclosure_event_field_name: string | null;
  disclosure_event_field_operator: string | null;
  disclosure_event_field_value: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * A match found during a scan
 */
export interface ConditionMatch {
  logId: string;
  ruleId: string;
  ruleName: string;
  conditionId: string;
  conditionExternalId: string;
  conditionTitle: string;
  conditionPriorTo: string;
  conditionCategory: string;
  conditionOwner: string;
  documentTitle: string;
  documentId: string;
  documentStatus: string;
  documentReceivedDate: string | null;
  action: ClearAction;
  requireConfirmation: boolean;
}

/**
 * Result of executing a clear
 */
export interface ClearingResult {
  conditionId: string;
  logId: string;
  success: boolean;
  conditionTitle?: string;
  error?: string;
}

/**
 * Audit log entry
 */
export interface ClearingLogEntry {
  id: string;
  ruleId: string | null;
  conditionId: string;
  conditionExternalId: string | null;
  conditionTitle: string;
  documentTitle: string;
  documentId: string;
  status: 'matched' | 'executed' | 'failed' | 'skipped';
  actionTaken: string | null;
  errorMessage: string | null;
  triggeredBy: 'manual' | 'webhook' | 'bulk';
  executedByUserId: string | null;
  executedAt: string | null;
  createdAt: string;
}

/**
 * Convert database row to domain model
 */
export function rowToAutoClearRule(row: ConditionAutoClearRuleRow): ConditionAutoClearRule {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    conditionMatchField: row.condition_match_field as ConditionMatchField,
    conditionMatchStrategy: row.condition_match_strategy as MatchStrategy,
    conditionMatchPattern: row.condition_match_pattern,
    documentMatchStrategy: row.document_match_strategy as MatchStrategy,
    documentMatchPattern: row.document_match_pattern,
    documentRequiredStatus: row.document_required_status,
    action: row.action as ClearAction,
    requireConfirmation: row.require_confirmation,
    priority: row.priority,
    isActive: row.is_active,
    disclosureCheckEnabled: row.disclosure_check_enabled,
    disclosureTypeRequired: row.disclosure_type_required,
    disclosureRecipientStatus: row.disclosure_recipient_status,
    disclosureEventFieldName: row.disclosure_event_field_name,
    disclosureEventFieldOperator: row.disclosure_event_field_operator,
    disclosureEventFieldValue: row.disclosure_event_field_value,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}
